import { getApiClient } from "../client";
import { PAYOUT_POPULATE, REFERRAL_CODE_POPULATE, REFERRAL_POPULATE } from "../populate";
import { DiscountWalletSchema, PartnerRequirementsSchema, PartnerStatsSchema, PayoutSchema, PayoutsWalletSchema, RecurringRequirementsSchema, ReferralCodeSchema, ReferralSchema, RewardsListSchema, } from "../schemas";
import { parseStrapiItemResponse, parseStrapiListResponse } from "../utils/parseStrapiResponse";
export const referralsEndpoints = {
    async getReferrals(params) {
        const populate = params?.populate ?? REFERRAL_POPULATE.DEFAULT;
        const response = await getApiClient().get("/referrals", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: ReferralSchema,
            populate,
            context: "referralsEndpoints.getReferrals",
        });
    },
    async getMyReferrals(params) {
        const populate = params?.populate ?? REFERRAL_POPULATE.DEFAULT;
        const response = await getApiClient().get("/referrals/me", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: ReferralSchema,
            populate,
            context: "referralsEndpoints.getMyReferrals",
        });
    },
};
export const referralCodesEndpoints = {
    async getMyReferralCodes(params) {
        const populate = params?.populate ?? REFERRAL_CODE_POPULATE.DEFAULT;
        const response = await getApiClient().get("/referral-codes/me", {
            params: { populate },
        });
        return parseStrapiListResponse(response.data, {
            schema: ReferralCodeSchema,
            populate,
            context: "referralCodesEndpoints.getMyReferralCodes",
        });
    },
    async getReferralCodes(params) {
        const populate = params?.populate ?? REFERRAL_CODE_POPULATE.DEFAULT;
        const response = await getApiClient().get("/referral-codes", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: ReferralCodeSchema,
            populate,
            context: "referralCodesEndpoints.getReferralCodes",
        });
    },
    async createReferralCode(input) {
        const response = await getApiClient().post("/referral-codes", {
            data: input,
        });
        return parseStrapiItemResponse(response.data, {
            schema: ReferralCodeSchema,
            context: "referralCodesEndpoints.createReferralCode",
        });
    },
    async deleteReferralCode(id) {
        const response = await getApiClient().delete(`/referral-codes/${id}`);
        return parseStrapiItemResponse(response.data);
    },
};
export const referrerEndpoints = {
    async getPartnerStats(userId) {
        const url = userId ? `/referrals/stats/${userId}` : "/referrals/stats/me";
        const response = await getApiClient().get(url);
        return parseStrapiItemResponse(response.data, {
            schema: PartnerStatsSchema,
            context: "referrerEndpoints.getPartnerStats",
        });
    },
    async getDiscountWallet() {
        const response = await getApiClient().get("/discounts/wallet");
        return parseStrapiItemResponse(response.data, {
            schema: DiscountWalletSchema,
            context: "referrerEndpoints.getDiscountWallet",
        });
    },
    async getRewards() {
        const response = await getApiClient().get("/rewards");
        return parseStrapiItemResponse(response.data, {
            schema: RewardsListSchema,
            context: "referrerEndpoints.getRewards",
        });
    },
    async getPayoutsWallet() {
        const response = await getApiClient().get("/payouts/wallet");
        return parseStrapiItemResponse(response.data, {
            schema: PayoutsWalletSchema,
            context: "referrerEndpoints.getPayoutsWallet",
        });
    },
    async getReferrerCode() {
        const response = await getApiClient().get("/referrer/me/referrer-code");
        return parseStrapiItemResponse(response.data);
    },
    async getRequirementsTo(referrerType, userId) {
        const userIdParam = userId ?? "me";
        const url = `/referrer/${userIdParam}/requirements/to/${referrerType}`;
        const response = await getApiClient().get(url);
        return parseStrapiItemResponse(response.data, {
            schema: PartnerRequirementsSchema,
            context: "referrerEndpoints.getRequirementsTo",
        });
    },
    async getRecurringRequirements(userId) {
        const userIdParam = userId ?? "me";
        const url = `/referrer/${userIdParam}/requirements`;
        const response = await getApiClient().get(url);
        return parseStrapiItemResponse(response.data, {
            schema: RecurringRequirementsSchema,
            context: "referrerEndpoints.getRecurringRequirements",
        });
    },
    async acceptTnC() {
        const response = await getApiClient().put("/referrer/accept-affiliate-tnc");
        return parseStrapiItemResponse(response.data);
    },
    async claimDiscountReward() {
        const response = await getApiClient().post("/rewards/claim");
        return parseStrapiItemResponse(response.data);
    },
    async claimPayoutReward(input) {
        const response = await getApiClient().post("/revolut/payouts/uncollected/link", { data: input });
        return parseStrapiItemResponse(response.data, {
            schema: PayoutSchema,
            context: "referrerEndpoints.claimPayoutReward",
        });
    },
    async becomeAmbassador() {
        const response = await getApiClient().put("/referrer/me/ambassador");
        return parseStrapiItemResponse(response.data);
    },
    async changePartnerType(input) {
        const { userId, referrerType } = input;
        const response = await getApiClient().put(`/referrer/${userId}/${referrerType}`);
        return parseStrapiItemResponse(response.data);
    },
    async blockFromAffiliate(userId) {
        const response = await getApiClient().put(`/referrer/${userId}/block`);
        return parseStrapiItemResponse(response.data);
    },
    async unblockFromAffiliate(userId) {
        const response = await getApiClient().put(`/referrer/${userId}/unblock`);
        return parseStrapiItemResponse(response.data);
    },
    async assignReferrer(input) {
        const response = await getApiClient().put("/referrals/admin/assign-referrer", { data: input });
        return parseStrapiItemResponse(response.data);
    },
    async removeReferrer(refereeId) {
        const response = await getApiClient().delete(`/referrals/admin/remove-referrer/${refereeId}`);
        return parseStrapiItemResponse(response.data);
    },
};
export const payoutsEndpoints = {
    async getPayouts(params) {
        const populate = params?.populate ?? PAYOUT_POPULATE.FULL;
        const response = await getApiClient().get("/payouts", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: PayoutSchema,
            populate,
            context: "payoutsEndpoints.getPayouts",
        });
    },
    async getMyPayouts(params) {
        const populate = params?.populate ?? PAYOUT_POPULATE.FULL;
        const response = await getApiClient().get("/payouts/me", {
            params: {
                populate,
                ...params,
            },
        });
        return parseStrapiListResponse(response.data, {
            schema: PayoutSchema,
            populate,
            context: "payoutsEndpoints.getMyPayouts",
        });
    },
    async downloadInvoice(payoutId) {
        return getApiClient().getBlob(`/payouts/${payoutId}/invoice`);
    },
    async downloadInvoices(payoutIds) {
        return getApiClient().postBlob("/payouts/invoices", { ids: payoutIds });
    },
    async getClaimLink(payoutId) {
        const response = await getApiClient().get(`/revolut/payouts/${payoutId}/claim-link`);
        return parseStrapiItemResponse(response.data);
    },
};
